import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  BookOpen,
  Play,
  Users,
  Heart,
  MessageCircle,
  Repeat2,
  Send,
  BarChart3,
  Clock,
  GraduationCap,
  Share2,
  Link2,
  CheckCircle,
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { api, showLoginModal } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import Avatar from '@/components/ui/Avatar'
import Badge from '@/components/ui/Badge'
import TabSwitch from '@/components/ui/TabSwitch'
import { getGradient, formatName, resolveImg } from '@/lib/helpers'

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins} минутын өмнө`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} цагийн өмнө`
  const days = Math.floor(hours / 24)
  return `${days} өдрийн өмнө`
}

/* ── Activity sidebar ── */
function ActivitySidebar({ teacherId }: { teacherId: string }) {
  const [activity, setActivity] = useState<any>(null)

  useEffect(() => {
    api.teachers.getActivity(teacherId).then(setActivity).catch(() => {})
  }, [teacherId])

  const summary = activity?.summary || { posts: 0, comments: 0, reviews: 0, courseViews: 0 }
  const daily = activity?.daily || []
  const totalPosts = activity?.totalPosts ?? summary.posts
  const totalComments = activity?.totalComments ?? summary.comments
  const totalReviews = activity?.totalReviews ?? summary.reviews
  const totalEnrollments = activity?.totalEnrollments ?? summary.courseViews

  return (
    <div className="w-full lg:w-[300px] shrink-0 flex flex-col gap-6">

      {/* Идэвхи section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="size-5 text-gray-600" />
          <span className="text-sm font-bold text-gray-800">Идэвхи</span>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between mb-5">
          <div className="text-center flex-1">
            <p className="text-xl font-bold text-gray-800">{totalPosts}</p>
            <p className="text-[10px] text-gray-400">нийтлэл</p>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-center flex-1">
            <p className="text-xl font-bold text-gray-800">{totalComments}</p>
            <p className="text-[10px] text-gray-400">хариулт</p>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-center flex-1">
            <p className="text-xl font-bold text-gray-800">{totalReviews}</p>
            <p className="text-[10px] text-gray-400">сэтгэгдэл</p>
          </div>
        </div>

        {/* Chart header */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500">Сүүлийн 30 хоног</span>
        </div>

        {/* Line chart */}
        <div className="h-[180px] -mx-2" style={{ minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={180} minWidth={0}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 9 }}
                tickFormatter={(v: string) => v.slice(5)}
                interval={6}
              />
              <YAxis tick={{ fontSize: 9 }} width={24} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                labelFormatter={(v) => String(v)}
              />
              <Line type="monotone" dataKey="posts" stroke="#6366f1" strokeWidth={2} dot={false} name="Нийтлэл" />
              <Line type="monotone" dataKey="comments" stroke="#22c55e" strokeWidth={2} dot={false} name="Хариулт" />
              <Line type="monotone" dataKey="reviews" stroke="#f97316" strokeWidth={2} dot={false} name="Сэтгэгдэл" />
              <Line type="monotone" dataKey="courseViews" stroke="#3b82f6" strokeWidth={2} dot={false} name="Үзэлт" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-3 mb-4">
          {[
            { color: '#6366f1', label: 'Нийтлэл' },
            { color: '#22c55e', label: 'Хариулт' },
            { color: '#f97316', label: 'Сэтгэгдэл' },
            { color: '#3b82f6', label: 'Үзэлт' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1">
              <div className="size-2 rounded-full" style={{ background: item.color }} />
              <span className="text-[10px] text-gray-500">{item.label}</span>
            </div>
          ))}
        </div>

        <hr className="border-gray-200 mb-4" />

        {/* Bottom stats */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-gray-400" />
              <span className="text-sm text-gray-600">Сургалт үзэлт</span>
            </div>
            <span className="text-sm font-bold text-gray-800">{totalEnrollments.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="size-4 text-gray-400" />
              <span className="text-sm text-gray-600">Нийт сэтгэгдэл</span>
            </div>
            <span className="text-sm font-bold text-gray-800">{totalReviews.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Post card ── */
function PostCard({
  post,
  teacherName,
  teacherAvatar,
}: {
  post: any
  teacherName: string
  teacherAvatar?: string
}) {
  const { user } = useAuth()
  const [liked, setLiked] = useState(
    Array.isArray(post.likes) && user
      ? post.likes.some((id: any) => (id?._id || id)?.toString() === user._id)
      : (post.isLiked || false)
  )
  const [likeCount, setLikeCount] = useState(
    Array.isArray(post.likes) ? post.likes.length : (post.likeCount || 0)
  )
  const [commentText, setCommentText] = useState('')
  const [comments, setComments] = useState<any[]>(post.comments || [])
  const [repostCount, setRepostCount] = useState(
    Array.isArray(post.reposts) ? post.reposts.length : (post.repostCount || 0)
  )
  const commentInputRef = useRef<HTMLInputElement>(null)
  const [showComments, setShowComments] = useState(false)
  const [sending, setSending] = useState(false)

  const handleLike = async () => {
    if( !user ) { showLoginModal(); return}
    try {
      const res = await api.posts.toggleLike(post._id || post.id)
      setLiked(res.liked)
      setLikeCount(res.likes)
    } catch {
      // 401 бол api.ts автомат login modal нээнэ — UI өөрчлөгдөхгүй
    }
  }

  const handleComment = async () => {
    if (!commentText.trim() || sending) return
    setSending(true)
    try {
      const res = await api.posts.addComment(post._id || post.id, commentText.trim())
      const newComment = { text: commentText.trim(), userId: { fullName: user?.fullName, avatar: user?.avatar }, createdAt: new Date().toISOString() }
      setComments(Array.isArray(res) ? res : (prev: any[]) => [...prev, newComment])
      setCommentText('')
    } catch {
      // 401 бол api.ts автомат login modal нээнэ
    } finally {
      setSending(false)
    }
  }

  const handleRepost = async () => {
    if( !user ) { showLoginModal(); return}
    try {
      await api.posts.toggleRepost(post._id || post.id)
      setRepostCount((c: number) => c + 1)
    } catch {}
  }

  const openComments = () => {
    setShowComments(true)
    setTimeout(() => commentInputRef.current?.focus(), 50)
  }

  const imageUrl = post.imageUrl || post.image
  const commentCount = comments.length || post.commentCount || 0

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-5 flex flex-col gap-4">
        {/* Author */}
        <div className="flex items-center gap-3">
          <Avatar src={teacherAvatar} alt={teacherName} size="sm" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">{teacherName}</span>
            <span className="text-xs text-gray-400">
              {post.createdAt ? timeAgo(post.createdAt) : post.timestamp || ''}
            </span>
          </div>
        </div>

        {/* Text */}
        <p className="text-sm text-gray-700 leading-relaxed">{post.content || post.text}</p>

        {/* Image */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="w-full rounded-xl object-cover max-h-[400px]"
          />
        )}

        {/* Stats */}
        {(likeCount > 0 || commentCount > 0 || repostCount > 0) && (
          <div className="flex items-center gap-4 text-xs text-gray-400">
            {likeCount > 0 && <span>{likeCount} таалагдсан</span>}
            {commentCount > 0 && (
              <button onClick={openComments} className="hover:text-gray-600 transition">
                {commentCount} сэтгэгдэл
              </button>
            )}
            {repostCount > 0 && <span className="ml-auto">{repostCount} хуваалцсан</span>}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-gray-200 px-5 py-3 flex items-center gap-6">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 text-sm font-medium transition cursor-pointer ${
            liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
          }`}
        >
          <Heart className={`size-4 ${liked ? 'fill-current' : ''}`} />
          Таалагдсан
        </button>
        <button
          onClick={openComments}
          className={`flex items-center gap-1.5 text-sm font-medium transition cursor-pointer ${
            showComments ? 'text-primary-500' : 'text-gray-500 hover:text-primary-500'
          }`}
        >
          <MessageCircle className="size-4" />
          Сэтгэгдэл
        </button>
        <button
          onClick={handleRepost}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-green-500 transition cursor-pointer ml-auto"
        >
          <Repeat2 className="size-4" />
          Хуваалцах
        </button>
      </div>

      {/* Comments section */}
      {showComments && (
        <>
          {/* Comments list */}
          {comments.length > 0 && (
            <div className="border-t border-gray-100 px-5 pt-4 pb-2 flex flex-col gap-3">
              {comments.map((c: any, i: number) => (
                <div key={c._id || i} className="flex gap-2.5">
                  <Link to={`/profile/${c.userId?._id || 'me'}`} className="shrink-0 mt-0.5">
                    <Avatar
                      src={resolveImg(c.userId?.avatar)}
                      alt={formatName(c.userId?.fullName) || ''}
                      size="sm"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 rounded-2xl px-3.5 py-2.5">
                      <Link
                        to={`/profile/${c.userId?._id || 'me'}`}
                        className="text-sm font-semibold text-gray-900 hover:text-primary-500 transition"
                      >
                        {formatName(c.userId?.fullName) || 'Хэрэглэгч'}
                      </Link>
                      <p className="text-sm text-gray-700 mt-0.5">{c.text}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-1">
                      {c.createdAt ? timeAgo(c.createdAt) : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Comment input */}
          <div className="border-t border-gray-100 px-5 py-3">
            {user ? (
              <div className="flex gap-2.5 items-center">
                <Avatar
                  src={resolveImg(user.avatar)}
                  alt={formatName(user.fullName) || ''}
                  size="sm"
                  className="shrink-0"
                />
                <input
                  ref={commentInputRef}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                  placeholder="Сэтгэгдэл бичих..."
                  className="flex-1 bg-gray-50 rounded-full px-4 py-2.5 text-sm outline-none text-gray-900 placeholder:text-gray-400 border border-gray-200 focus:border-primary-500 transition"
                />
                <button
                  onClick={handleComment}
                  disabled={!commentText.trim() || sending}
                  className="bg-primary-500 text-white rounded-full p-2.5 hover:bg-primary-600 transition disabled:opacity-50 shrink-0 cursor-pointer"
                >
                  <Send className="size-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={showLoginModal}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                Сэтгэгдэл бичихийн тулд нэвтэрнэ үү
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/* ── Main page ── */
export default function TeacherDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [teacher, setTeacher] = useState<any>(null)
  const [courses, setCourses] = useState<any[]>([])
  const [posts, setPosts] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState('posts')
  const [following, setFollowing] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [shareToast, setShareToast] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!id) return
    api.teachers.getById(id).then((t) => {
      setTeacher(t)
      setFollowing(t.isFollowing || false)
    }).catch(console.error)
    api.teachers.getCourses(id).then(setCourses).catch(() => {})
    api.teachers.getPosts(id).then(setPosts).catch(() => {})
  }, [id])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
      document.removeEventListener('click', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])
    
  if (!teacher) {
    return (
      <div className="max-w-[1024px] mx-auto px-4 py-16 text-center text-gray-400">
        Ачаалж байна...
      </div>
    )
  }

  const teacherName = formatName(teacher.userId?.fullName) || formatName(teacher.fullName) || teacher.name || ''
  const teacherAvatar = resolveImg(teacher.userId?.avatar || teacher.avatar)
  const teacherTitle = teacher.title || teacher.specialty || ''
  const bio = teacher.description || teacher.bio || ''
  const courseCount = teacher.courseCount ?? courses.length
  const videoCount = teacher.videoCount ?? 0
  const postCount = teacher.postCount ?? posts.length
  const followerCount = teacher.followerCount ?? teacher.studentCount ?? 0
  const isOwnProfile = user && teacher.userId?._id === user._id

  const handleFollow = async () => {
    if (!teacher.userId?._id) return
    try {
      const res = await api.users.toggleFollow(teacher.userId._id)
      setFollowing(res.following ?? !following)
    } catch {
      // 401 triggers auth modal automatically
    }
  }
  const handleNativeShare = async () => {
    setDropdownOpen(false)
    const url = `${window.location.origin}/teachers/${id}`
    try {
      await navigator.share({ title: `${teacherName} багшийн профайл`, url })
    } catch {}
  }

  const handleCopyLink = async () => {
    setDropdownOpen(false)
    const url = `${window.location.origin}/teachers/${id}`
    try {
      await navigator.clipboard.writeText(url)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
    } catch {
      window.prompt('Холбоосыг хуулна уу:', url)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="max-w-[1024px] mx-auto px-4 flex items-center justify-between py-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-white border border-gray-200 rounded-full px-4 py-2.5 flex items-center gap-2.5 text-sm font-medium text-gray-800 shadow-md hover:bg-gray-50 transition cursor-pointer"
          >
            <ArrowLeft className="size-5" />
            Буцах
          </button>
          <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((v) => !v)}
            className="bg-white border border-gray-200 rounded-full p-2.5 shadow-md hover:bg-gray-50 transition cursor-pointer"
          >
            <Share2 className="size-5 text-gray-600" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-12 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {typeof navigator.share === 'function' && (
                  <button
                    onClick={handleNativeShare}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <Share2 className="size-4 text-gray-400" />
                    Хуваалцах
                  </button>
                )}
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  {shareToast ? (
                    <>
                      <span className="size-4 text-green-500">✓</span>
                      Хуулагдлаа
                    </>
                  ) : (
                    <>
                      <Link2 className="size-4 text-gray-400" />
                      Линк хуулах
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Teacher header */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {/* Avatar */}
          <Avatar src={teacherAvatar} alt={teacherName} size="xl" />

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{teacherName}</h1>
              <Badge variant="gray">багш</Badge>
              {teacherTitle && (
                <Badge variant="accent">{teacherTitle}</Badge>
              )}
            </div>

            <p className="text-sm text-gray-600 leading-relaxed max-w-lg mb-4">
              {bio}
            </p>

            {/* Stats */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-1.5">
                <BookOpen className="size-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-900">{courseCount}</span>
                <span className="text-sm text-gray-500">сургалт</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Play className="size-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-900">{videoCount}</span>
                <span className="text-sm text-gray-500">видео</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MessageCircle className="size-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-900">{postCount}</span>
                <span className="text-sm text-gray-500">нийтлэл</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="size-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-900">{followerCount}</span>
                <span className="text-sm text-gray-500">дагагч</span>
              </div>
            </div>
          </div>

          {/* Action button */}
          {isOwnProfile ? (
            <Link
              to="/settings"
              className="bg-accent-500 text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-accent-600 transition shrink-0"
            >
              Профайл засах
            </Link>
          ) : (
            <button
              onClick={handleFollow}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition shrink-0 cursor-pointer ${
                following
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-accent-500 text-white hover:bg-accent-600'
              }`}
            >
              {following ? 'Дагасан' : 'Дагах'}
            </button>
          )}
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-[1024px] mx-auto px-4 pb-4">
        <div className="max-w-md">
          <TabSwitch
            tabs={[
              { label: 'Сургалтууд', value: 'courses' },
              { label: 'Нийтлэлүүд', value: 'posts' },
            ]}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </div>
      </section>

      {/* Tab content */}
      <section className="max-w-[1024px] mx-auto px-4 pb-16">
        {activeTab === 'courses' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {courses.length === 0 && (
              <p className="col-span-3 text-center py-12 text-gray-400">Сургалт байхгүй</p>
            )}
            {courses.map((course, idx) => (
              <Link key={course._id || idx} to={`/courses/${course._id}`} className="group">
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition">
                  <div className={`h-36 bg-gradient-to-br ${getGradient(idx)} relative flex items-center justify-center`}>
                    {(course.thumbnail || course.thumbnailUrl) ? (
                      <img src={resolveImg(course.thumbnail || course.thumbnailUrl)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <BookOpen className="size-10 text-gray-400/40" />
                    )}
                  </div>
                  <div className="p-4">
                    {course.category && (
                      <Badge variant="primary" className="mb-2">{course.category}</Badge>
                    )}
                    <h3 className="font-medium text-sm text-gray-900 group-hover:text-primary-500 transition">
                      {course.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{teacherName}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {activeTab === 'posts' && (
          <div className="flex flex-col lg:flex-row gap-6 mt-4">
            {/* Posts feed */}
            <div className="flex-1 min-w-0 flex flex-col gap-4">
              {posts.length === 0 && (
                <p className="text-center py-12 text-gray-400">Нийтлэл байхгүй</p>
              )}
              {posts.map((post, idx) => (
                <PostCard
                  key={post._id || idx}
                  post={post}
                  teacherName={teacherName}
                  teacherAvatar={teacherAvatar}
                />
              ))}
            </div>

            {/* Right sidebar */}
            <ActivitySidebar teacherId={id!} />
          </div>
        )}
      </section>
      {shareToast && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50"
          style={{ animation: 'shareToastIn 0.25s ease-out' }}
        >
          <div className="bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full shadow-lg flex items-center gap-2">
            <CheckCircle className="size-4 text-green-400" />
            Холбоос хуулагдлаа
          </div>
        </div>
      )}
    </div>
  )
}
